import Order from 'communication/types/order/Order';
import {
    OutliersBenchmarkType,
    OutliersPlType,
} from 'widgets/outliers/store/config/OutliersConfigState';
import orderBy from 'lodash/orderBy';
import isNil from 'lodash/isNil';
import {
    adjustArrivalPrice,
    adjustedArrivalPriceBenchmarkStrategy,
} from 'widgets/outliers/data/benchmarksStrategies/adjustedArrivalPriceBenchmarkStrategy';
import { arrivalPriceBenchmarkStrategy } from 'widgets/outliers/data/benchmarksStrategies/arrivalPriceBenchmark';
import { realisedPnlStrategy } from 'widgets/outliers/data/pnlStrategies/realised';
import { unrealisedPnlStrategy } from 'widgets/outliers/data/pnlStrategies/unrealised';
import { totalPnlStrategy } from 'widgets/outliers/data/pnlStrategies/total';
import { OutliersPnlData } from 'widgets/outliers/data/pnlStrategies';

export interface OutliersOrderData {
    value: number;
    /*
     * This is the value used in the chart and will equal realisedPnl, unrealisedPnl or totalPnl depending on the plType setting
     */
    pnl?: number;
    /*
     * This is used for ordering
     */
    absPnl?: number;
    realisedPnl?: number;
    unrealisedPnl?: number;
    totalPnl?: number;
    benchmarkPerformance?: number;
    /*
     * Either arrivalPD or arrivalPAdj depending on the benchmarkType setting
     */
    benchmarkPrice?: number;
    /*
     * May need the calculated arrivalPAdj for this grid
     */
    arrivalPAdj?: number;
    arrivalPDAdj?: number;
}

export type OutliersDataItem = Order & OutliersOrderData;

export function isOutliersDataItem(item: unknown): item is OutliersDataItem {
    return !isNil((item as OutliersDataItem).pnl);
}

export interface OutliersData {
    ordersCount?: number;
    date: number;
    items: OutliersDataItem[];
}

export function buildOutliersData(
    orders: Order[],
    benchmarkType: OutliersBenchmarkType,
    plType: OutliersPlType,
    numberOfOrders: number,

    exchangeRate: number
): OutliersData {
    const allItems = orders.map((o) => ({
        ...o,
        ...mapOrder(o, benchmarkType, plType, exchangeRate),
    }));
    return buildOutliersDataFromItems(allItems, numberOfOrders);
}

export function buildOutliersDataFromItems(
    allItems: OutliersOrderData[],
    numberOfOrders: number
) {
    const allItemsWithValues = allItems.filter(
        (i) =>
            !isNil(i.benchmarkPrice) &&
            !isNil(i.pnl) &&
            !isNil(i.benchmarkPerformance)
    ) as OutliersDataItem[];

    const byDescendingAbsPnl = orderBy(
        allItemsWithValues,
        ['absPnl'],
        ['desc']
    );
    const items = byDescendingAbsPnl.splice(0, numberOfOrders);

    return { items, date: new Date().getTime(), ordersCount: allItems.length };
}

export function mapOrder(
    order: Order,
    benchmarkType: OutliersBenchmarkType,
    plType: OutliersPlType,

    exchangeRate: number
): OutliersOrderData {
    const value = (order.qty ?? 0) * (order.pncD ?? 0) * exchangeRate;

    const arrivalPBenchmark = arrivalPriceBenchmarkStrategy.mapOrder(
        order,
        exchangeRate
    );
    const arrivalPAdjBenchmark = adjustedArrivalPriceBenchmarkStrategy.mapOrder(
        order,

        exchangeRate
    );

    const benchmarkPrice =
        benchmarkType === 'arrivalPrice'
            ? arrivalPBenchmark?.benchmarkPrice
            : arrivalPAdjBenchmark?.benchmarkPrice;
    const arrivalPDAdj = arrivalPAdjBenchmark?.benchmarkPrice;
    const arrivalPAdj = adjustArrivalPrice(order, order?.arrivalP);

    const applySide: (a?: OutliersPnlData) => OutliersPnlData | undefined = (
        a
    ) => {
        if (a && order.side === 'B') {
            a.pnl = a.pnl * -1;
            a.benchmarkPerformance = a.benchmarkPerformance * -1;
        }
        return a;
    };
    const realisedPnlData = applySide(
        realisedPnlStrategy.mapOrder(order, benchmarkPrice, exchangeRate)
    );
    const unRealisedPnlData = applySide(
        unrealisedPnlStrategy.mapOrder(order, benchmarkPrice, exchangeRate)
    );
    const totalPnlData = applySide(
        totalPnlStrategy.mapOrder(order, benchmarkPrice, exchangeRate)
    );

    const chartPnlData =
        plType === 'realised'
            ? realisedPnlData
            : plType === 'unrealised'
            ? unRealisedPnlData
            : totalPnlData;
    const benchmarkPerformance = chartPnlData?.benchmarkPerformance;

    const pnl = chartPnlData?.pnl;
    const realisedPnl = realisedPnlData?.pnl;
    const unrealisedPnl = unRealisedPnlData?.pnl;
    const totalPnl = totalPnlData?.pnl;
    const absPnl = isNil(pnl) ? undefined : Math.abs(pnl);

    return {
        value,
        benchmarkPrice,
        benchmarkPerformance,
        pnl,
        realisedPnl,
        unrealisedPnl,
        totalPnl,
        absPnl,
        arrivalPAdj,
        arrivalPDAdj,
    };
}
